import React, { useState, useEffect, useCallback } from 'react';
import { FiPlus, FiPackage, FiChevronLeft } from 'react-icons/fi';
import { Modal } from 'components/basicos';
import API, { getImagenUrl } from 'shared/api';
import { fmtMonto } from 'shared/format';

// Expande los componentes del combo en instancias individuales (cantidad > 1 =>
// una entrada por unidad), igual que el catalogo del cliente.
const instanciasCombo = (items) => {
  const out = [];
  (items || []).forEach((it) => {
    const cant = Math.max(1, parseInt(it.cantidad, 10) || 1);
    for (let idx = 0; idx < cant; idx++) {
      out.push({ ...it, _instancia: idx });
    }
  });
  return out;
};

const ComboBuscadorModal = ({ open, onClose, onAgregarCombo }) => {
  const [combos, setCombos] = useState([]);
  const [cargando, setCargando] = useState(false);
  const [comboSel, setComboSel] = useState(null);
  const [selEtiquetas, setSelEtiquetas] = useState({});
  const [agregando, setAgregando] = useState(false);

  useEffect(() => {
    if (!open) return;
    setComboSel(null);
    setCargando(true);
    API.get('/pedidos/combos/listar')
      .then((r) => {
        if (r.data.status === 'success') setCombos(r.data.data || []);
      })
      .catch(() => {})
      .finally(() => setCargando(false));
  }, [open]);

  const abrirCombo = useCallback((combo) => {
    const sel = {};
    instanciasCombo(combo.items).forEach((it) => {
      sel[`${it.codigo_producto}#${it._instancia}`] = (it.etiquetas || []).map((e) => e.id);
    });
    setSelEtiquetas(sel);
    setComboSel(combo);
  }, []);

  const toggleEtiqueta = (codigoProducto, instanciaIdx, et) => {
    const key = `${codigoProducto}#${instanciaIdx}`;
    setSelEtiquetas((prev) => {
      const actual = prev[key] || [];
      const ya = actual.includes(et.id);
      return {
        ...prev,
        [key]: ya ? actual.filter((id) => id !== et.id) : [...actual, et.id],
      };
    });
  };

  const confirmarAgregar = () => {
    if (!comboSel) return;
    setAgregando(true);
    let extra = 0;
    const componentes = instanciasCombo(comboSel.items).map((it) => {
      const incluidos = new Set((it.etiquetas || []).map((e) => e.id));
      const seleccionados = selEtiquetas[`${it.codigo_producto}#${it._instancia}`] || [];
      const etiquetas = (it.etiquetas_disponibles || [])
        .filter((e) => seleccionados.includes(e.id))
        .map((e) => ({ id: e.id, nombre: e.nombre, precio: e.precio || 0 }));
      (it.etiquetas_disponibles || []).forEach((e) => {
        if (!incluidos.has(e.id) && seleccionados.includes(e.id)) extra += e.precio || 0;
      });
      return {
        codigo_producto: it.codigo_producto,
        nombre: it.nombre,
        etiquetas,
        etiquetas_disponibles: it.etiquetas_disponibles || [],
      };
    });
    const prod = {
      codigo: `combo_${comboSel.id}`,
      combo_id: comboSel.id,
      nombre: comboSel.nombre,
      descripcion: comboSel.nombre,
      precio: comboSel.precio,
      stock: 999999,
      pesable: false,
      imagen_url: comboSel.imagen_url || '',
      componentes,
      combo_extra: extra,
    };
    onAgregarCombo(prod);
    setAgregando(false);
    setComboSel(null);
    onClose();
  };

  return (
    <Modal open={open} onClose={onClose} title={comboSel ? 'Personalizar combo' : 'Agregar combo'} width="lg">
      {!comboSel ? (
        <div style={{ display: 'flex', flexDirection: 'column', minHeight: 0, maxHeight: '60vh', overflowY: 'auto' }}>
          {cargando && (
            <div style={{ textAlign: 'center', padding: 20, fontSize: 13, color: 'var(--agx-muted)' }}>
              Cargando combos...
            </div>
          )}
          {!cargando && combos.length === 0 && (
            <div style={{ textAlign: 'center', padding: 20, fontSize: 13, color: 'var(--agx-muted)' }}>
              No hay combos disponibles
            </div>
          )}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            {combos.map((c) => (
              <div
                key={c.id}
                onClick={() => abrirCombo(c)}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '10px',
                  padding: '10px 14px',
                  borderRadius: '10px',
                  border: '1px solid var(--agx-border)',
                  cursor: 'pointer',
                  background: 'var(--agx-surface)',
                }}
              >
                <div
                  style={{
                    width: 40,
                    height: 40,
                    borderRadius: '8px',
                    flexShrink: 0,
                    background: 'var(--agx-skeleton)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    overflow: 'hidden',
                  }}
                >
                  {c.imagen_url ? (
                    <img src={getImagenUrl(c.imagen_url)} alt={c.nombre} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                  ) : (
                    <FiPackage size={18} color="var(--agx-faint)" />
                  )}
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--agx-text)' }}>{c.nombre}</div>
                  <div style={{ fontSize: 11, color: 'var(--agx-muted)' }}>{(c.items || []).length} componentes</div>
                </div>
                <span style={{ fontSize: 14, fontWeight: 700, color: 'var(--agx-pri2)' }}>${fmtMonto(c.precio)}</span>
                <FiPlus size={16} color="var(--agx-pri2)" />
              </div>
            ))}
          </div>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', minHeight: 0 }}>
          <button
            onClick={() => setComboSel(null)}
            style={{
              border: 'none',
              background: 'transparent',
              cursor: 'pointer',
              color: 'var(--agx-pri2)',
              fontSize: 13,
              fontWeight: 700,
              display: 'flex',
              alignItems: 'center',
              gap: '4px',
              padding: 0,
              marginBottom: '10px',
              fontFamily: 'inherit',
            }}
          >
            <FiChevronLeft size={16} /> Volver
          </button>
          <div style={{ fontSize: 15, fontWeight: 700, color: 'var(--agx-text)', marginBottom: '4px' }}>
            {comboSel.nombre}
          </div>
          <div style={{ fontSize: 12, color: 'var(--agx-muted)', marginBottom: '12px' }}>
            Componentes y personalizaciones
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', maxHeight: '48vh', overflowY: 'auto' }}>
            {instanciasCombo(comboSel.items).map((it) => {
              const key = `${it.codigo_producto}#${it._instancia}`;
              const seleccionados = selEtiquetas[key] || [];
              return (
                <div key={key} style={{ padding: '10px 12px', borderRadius: '10px', border: '1px solid var(--agx-border)' }}>
                  <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--agx-text)' }}>{it.nombre}</div>
                  {(it.etiquetas_disponibles || []).length > 0 && (
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', marginTop: '6px' }}>
                      {it.etiquetas_disponibles.map((et) => {
                        const sel = seleccionados.includes(et.id);
                        return (
                          <button
                            key={et.id}
                            onClick={() => toggleEtiqueta(it.codigo_producto, it._instancia, et)}
                            style={{
                              padding: '4px 9px',
                              borderRadius: '999px',
                              cursor: 'pointer',
                              border: sel ? '2px solid var(--agx-pri2)' : '1px solid var(--agx-border)',
                              background: sel ? 'var(--agx-pri2)' : 'var(--agx-surface)',
                              color: sel ? '#fff' : 'var(--agx-text)',
                              fontSize: 11,
                              fontWeight: sel ? 700 : 500,
                              fontFamily: 'inherit',
                            }}
                          >
                            {et.nombre}
                            {et.precio > 0 && <span style={{ marginLeft: 4, fontWeight: 700 }}>+${fmtMonto(et.precio)}</span>}
                          </button>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
          <button
            onClick={confirmarAgregar}
            disabled={agregando}
            style={{
              marginTop: '12px',
              padding: '12px',
              borderRadius: '10px',
              border: 'none',
              background: 'var(--agx-pri2)',
              color: '#fff',
              fontSize: 14,
              fontWeight: 700,
              cursor: agregando ? 'default' : 'pointer',
              opacity: agregando ? 0.6 : 1,
              fontFamily: 'inherit',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '6px',
            }}
          >
            <FiPlus size={16} /> Agregar combo · ${fmtMonto(comboSel.precio)}
          </button>
        </div>
      )}
    </Modal>
  );
};

export default ComboBuscadorModal;
